import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import MediaLibraryManager from './MediaLibraryManager';
import ImageCropEditor, {cropPreviewUrl} from './ImageCropEditor';
import {createSellerMediaApi} from './mediaApi';
import {createCropApi, cropMessage} from './cropApi';
import {CROP_PRESETS} from '../../../../../shared/mediaCrop.js';

const slots = {
  background: ['Background', 'backgroundImage', 'background'],
  profile: ['Profile circle', 'profilePicture', 'profile'],
  bannerDesktop: ['Desktop banner', 'bannerImage', 'banner-desktop'],
  bannerMobile: ['Mobile banner', 'bannerImage', 'banner-mobile'],
  social: ['Social share', 'socialImage', 'social'],
};
export default function MediaCropHarness({cropStagingEnabled = false, products = []}) {
  const auth = useAuth();
  const api = useMemo(() => createCropApi(auth), [auth.fetch, auth.API_URL]);
  const media = useMemo(() => createSellerMediaApi({fetch: auth.fetch, apiUrl: auth.API_URL}), [auth.fetch, auth.API_URL]);
  const [files, setFiles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [target, setTarget] = useState('background');
  const [productId, setProductId] = useState(String(products[0]?.id || ''));
  const [editing, setEditing] = useState(null);
  const [picker, setPicker] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [pending, setPending] = useState(false);
  const [revision, setRevision] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const contextVersion = useRef(0);
  const [productCrops, setProductCrops] = useState({});
  const [product, setProduct] = useState(null);
  const preset = target === 'product' ? 'product-listing' : slots[target][2];
  const source = target === 'product' ? files.find(file => file.id === product?.imageId)
    : settings?.basics?.[slots[target][1]];
  const saved = target === 'product' ? (Object.hasOwn(productCrops, productId) ? productCrops[productId]
    : files.find(file => file.id === product?.cropFileId)) : settings?.basics?.imageryCrops?.[target]?.file;

  useEffect(() => {
    if (!cropStagingEnabled) return;
    let active = true;
    Promise.all([api.storefront(), media.list()]).then(([nextSettings, nextFiles]) => {
      if (!active) return;
      setSettings(nextSettings.data); setFiles(nextFiles); setError('');
    }).catch(failure => { if (active) setError(cropMessage(failure)); });
    return () => {active = false;};
  }, [api, media, cropStagingEnabled, refresh]);

  useEffect(() => {
    if (!cropStagingEnabled || target !== 'product' || !productId) return;
    let active = true;
    setProduct(null);
    api.product(productId).then(result => {
      if (!active) return;
      const next = result.data;
      setProduct({...next, cropFileId: next.metadata?.listingImageCrop?.sourceFileId === next.imageId
        ? next.metadata.listingImageCrop.fileId : null});
      setProductCrops(current => {
        const updated = {...current}; delete updated[productId]; return updated;
      });
    }).catch(failure => {if (active) setError(cropMessage(failure));});
    return () => {active = false;};
  }, [api, cropStagingEnabled, target, productId, refresh]);

  if (!cropStagingEnabled) return null;
  const changeContext = action => {
    contextVersion.current++; action(); setProduct(null); setCandidate(null); setStatus(''); setError('');
  };
  const attach = async file => {
    const version = contextVersion.current;
    setPending(true); setError('');
    try {
      const result = target === 'product' ? await api.attachProduct(productId, file) : await api.attachStorefront(target, file);
      if (version !== contextVersion.current) return;
      if (target === 'product') setProductCrops(current => ({...current, [productId]: file}));
      else setSettings(current => ({...current, basics: result.data.basics}));
      setCandidate(null); setStatus(file ? 'Placement saved. Public rendering is still dormant.' : 'Placement reset; the reusable copy remains in the library.');
    } catch (failure) { if (version === contextVersion.current) setError(cropMessage(failure)); }
    finally {setPending(false);}
  };
  const created = file => {
    setFiles(current => [file, ...current]); setCandidate(file); setEditing(null); setRevision(value => value + 1);
    setStatus('Copy saved to the library. Review it, then attach explicitly. An attachment retry reuses this copy.');
  };
  return <div className="crop-staging-harness">
    <h2>Reusable image crops · internal staging</h2>
    <p>These controls write real library copies and placements for the active channel. Public crop rendering awaits review and launch.</p>
    <button type="button" disabled={pending || !!editing || picker} onClick={() => setRefresh(value => value + 1)}>Refresh saved state and library</button>
    {error && <p role="alert">{error}</p>}{status && <p role="status">{status}</p>}
    {!settings && <p role="status">Loading active-channel imagery…</p>}
    <fieldset disabled={pending || !!editing || picker}>
      <legend>Placement</legend>
      <label>Target <select value={target} onChange={event => changeContext(() => setTarget(event.target.value))}>
        {Object.entries(slots).map(([key, values]) => <option value={key} key={key}>{values[0]}</option>)}
        <option value="product">Product listing thumbnail</option>
      </select></label>
      {target === 'product' && <label>Product <select value={productId} onChange={event => changeContext(() => setProductId(event.target.value))}>
        {products.map(item => <option value={item.id} key={item.id}>{item.name} (#{item.id})</option>)}
      </select></label>}
      <p>Preset: {preset}. Full product images and source imagery remain intact.</p>
      <button type="button" disabled={!source} onClick={() => setEditing(source)}>Crop current original</button>
      <button type="button" disabled={!saved} onClick={() => setEditing(saved)}>Adjust saved copy</button>
      <button type="button" onClick={() => setPicker(true)}>Choose or create a reusable copy</button>
      <button type="button" disabled={target === 'product' && !product} onClick={() => attach(null)}>Reset placement</button>
    </fieldset>
    <div className="crop-staging-placements">
      <div className="crop-staging-placement"><h3>Original / full image</h3>
        {source ? <img src={cropPreviewUrl(source)} alt="Original source" /> : <p>Set this source in the existing editor first.</p>}
      </div>
      <div className="crop-staging-placement"><h3>Saved placement</h3>
        {saved ? <div className={`crop-staging-preview crop-staging-preview-${preset}`} style={{aspectRatio: CROP_PRESETS[preset].ratio}}><img src={cropPreviewUrl(saved)} alt="Saved crop placement" /></div> : <p>No crop loaded for this placement.</p>}
      </div>
      <div className="crop-staging-placement"><h3>Candidate · not attached</h3>
        {candidate && <><div className={`crop-staging-preview crop-staging-preview-${preset}`} style={{aspectRatio: CROP_PRESETS[preset].ratio}}><img src={cropPreviewUrl(candidate)} alt="Unattached candidate crop" /></div>
          <button type="button" disabled={pending || (target === 'product' && !product)} onClick={() => attach(candidate)}>Attach this copy</button>
          <button type="button" disabled={pending} onClick={() => setCandidate(null)}>Discard selection (keep library copy)</button></>}
      </div>
    </div>
    <details><summary>Placement debug state</summary><pre>{JSON.stringify({target, productId: target === 'product' ? productId : null,
      sourceId: source?.id, savedUuid: saved?.uuid, candidateUuid: candidate?.uuid, imageryCrops: settings?.basics?.imageryCrops}, null, 2)}</pre></details>
    <p>Public storefront: {settings?.url && <a href={settings.url} target="_blank" rel="noreferrer">Open saved public storefront</a>}. This public view does not include staged crops or unsaved framing.</p>
    <h3>Full media library · reusable crop creation</h3>
    <p>Product previews use the 4:3 listing frame; classic Green storefronts use contain and can look different. The native app does not yet read this crop.</p>
    <fieldset disabled={pending}><legend>Library tools</legend>
      <MediaLibraryManager key={revision} cropControls={{onSave: created}} />
    </fieldset>
    {editing && <ImageCropEditor file={editing} files={files} preset={preset} onCancel={() => setEditing(null)} onSave={created} />}
    {picker && <MediaLibraryManager variant="modal" selectable accept="image/jpeg,image/png,image/webp"
      title={`Choose ${preset} copy`} cropControls={{preset, onSave: file => {created(file); setPicker(false);}}}
      onSelect={file => {setCandidate(file); setPicker(false);}} onCancel={() => setPicker(false)} />}
  </div>;
}
